import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const BlogPost3: React.FC = () => {
  useEffect(() => {
    document.title = "WEBP vs JPG: Which Image Format Should You Use? | PixelShift Blog";
  }, []);

  return (
    <div className="min-h-screen bg-gray-900 text-gray-200 pt-24 pb-16 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8 text-sm">
          <Link to="/" className="text-brand-primary hover:underline">Home</Link>
          <span className="mx-2 text-gray-600">→</span>
          <Link to="/blog" className="text-brand-primary hover:underline">Blog</Link>
          <span className="mx-2 text-gray-600">→</span>
          <span className="text-gray-400">WEBP vs JPG</span>
        </div>

        <article>
          <header className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <span className="px-3 py-1 bg-brand-primary/20 text-brand-primary rounded-full text-sm font-medium">Comparison</span>
              <span className="text-gray-500">January 22, 2025</span>
              <span className="text-gray-500">•</span>
              <span className="text-gray-500">6 min read</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              WEBP vs JPG: Which Image Format Should You Use?
            </h1>
            
            <p className="text-xl text-gray-400">
              Understanding the key differences between WEBP and JPG formats to choose the right one for your needs.
            </p>
          </header>

          <div className="prose prose-invert prose-lg max-w-none">
            <h2 className="text-2xl font-bold text-white mt-8 mb-4">The Quick Answer</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-gradient-to-br from-brand-primary to-brand-secondary p-6 rounded-2xl">
                <h3 className="text-2xl font-bold text-white mb-3">Use WEBP for:</h3>
                <ul className="space-y-2 text-white">
                  <li>• Website images</li>
                  <li>• Blog graphics</li>
                  <li>• Online stores</li>
                  <li>• Fast-loading pages</li>
                </ul>
              </div>
              <div className="bg-gray-800 p-6 rounded-2xl border-2 border-gray-700">
                <h3 className="text-2xl font-bold text-white mb-3">Use JPG for:</h3>
                <ul className="space-y-2 text-gray-300">
                  <li>• Emails & documents</li>
                  <li>• Print materials</li>
                  <li>• Social media</li>
                  <li>• Universal compatibility</li>
                </ul>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-white mt-12 mb-4">File Size Comparison</h2>
            <p className="text-gray-300 mb-4">
              The main advantage of WEBP is significantly smaller file sizes:
            </p>

            <div className="bg-gray-800 p-8 rounded-2xl mb-8">
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-white font-bold">Original PNG</span>
                    <span className="text-red-400 font-bold">2.4 MB</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-4">
                    <div className="bg-red-500 h-4 rounded-full" style={{width: '100%'}}></div>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-white font-bold">JPG (80% quality)</span>
                    <span className="text-yellow-400 font-bold">856 KB</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-4">
                    <div className="bg-yellow-500 h-4 rounded-full" style={{width: '36%'}}></div>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-white font-bold">WEBP (80% quality)</span>
                    <span className="text-green-400 font-bold">524 KB</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-4">
                    <div className="bg-green-500 h-4 rounded-full" style={{width: '22%'}}></div>
                  </div>
                </div>
              </div>
              <p className="text-center text-gray-400 mt-6 text-sm">
                💡 WEBP is 39% smaller than JPG at the same quality
              </p>
            </div>

            <h2 className="text-2xl font-bold text-white mt-12 mb-4">Feature Comparison</h2>
            <div className="overflow-x-auto mb-8">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b-2 border-gray-700">
                    <th className="p-4 text-left text-white">Feature</th>
                    <th className="p-4 text-center text-green-400">WEBP</th>
                    <th className="p-4 text-center text-blue-400">JPG</th>
                  </tr>
                </thead>
                <tbody className="text-gray-300">
                  <tr className="border-b border-gray-800">
                    <td className="p-4 font-semibold">File Size</td>
                    <td className="p-4 text-center text-green-400">✅ 25-35% smaller</td>
                    <td className="p-4 text-center">Standard</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="p-4 font-semibold">Quality</td>
                    <td className="p-4 text-center text-green-400">✅ Better</td>
                    <td className="p-4 text-center text-blue-400">✅ Good</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="p-4 font-semibold">Transparency</td>
                    <td className="p-4 text-center text-green-400">✅ Yes</td>
                    <td className="p-4 text-center text-red-400">❌ No</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="p-4 font-semibold">Animation</td>
                    <td className="p-4 text-center text-green-400">✅ Yes</td>
                    <td className="p-4 text-center text-red-400">❌ No</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="p-4 font-semibold">Browser Support</td>
                    <td className="p-4 text-center">97% (modern)</td>
                    <td className="p-4 text-center text-blue-400">✅ 100%</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="p-4 font-semibold">Software Support</td>
                    <td className="p-4 text-center text-yellow-400">⚠️ Limited</td>
                    <td className="p-4 text-center text-blue-400">✅ Universal</td>
                  </tr>
                  <tr>
                    <td className="p-4 font-semibold">SEO Impact</td>
                    <td className="p-4 text-center text-green-400">✅ Better (faster loading)</td>
                    <td className="p-4 text-center text-blue-400">✅ Good</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2 className="text-2xl font-bold text-white mt-12 mb-4">When to Use WEBP</h2>
            <div className="bg-green-900/20 border-l-4 border-green-500 p-6 rounded-r-lg mb-6">
              <ul className="space-y-3 text-gray-300">
                <li>✅ <strong>Website optimization</strong> - Faster page loads = better SEO</li>
                <li>✅ <strong>E-commerce product images</strong> - Save bandwidth, load faster</li>
                <li>✅ <strong>Blog graphics</strong> - Smaller files, same quality</li>
                <li>✅ <strong>Mobile-first sites</strong> - Critical for data-conscious users</li>
                <li>✅ <strong>Hero images</strong> - Large images benefit most from compression</li>
              </ul>
            </div>

            <h2 className="text-2xl font-bold text-white mt-12 mb-4">When to Use JPG</h2>
            <div className="bg-blue-900/20 border-l-4 border-blue-500 p-6 rounded-r-lg mb-6">
              <ul className="space-y-3 text-gray-300">
                <li>✅ <strong>Email attachments</strong> - Universal compatibility</li>
                <li>✅ <strong>Social media</strong> - Some platforms don't support WEBP</li>
                <li>✅ <strong>Print materials</strong> - Standard for printers</li>
                <li>✅ <strong>Documents (Word, PowerPoint)</strong> - WEBP not supported</li>
                <li>✅ <strong>Photography portfolios</strong> - Widely accepted format</li>
              </ul>
            </div>

            <h2 className="text-2xl font-bold text-white mt-12 mb-4">Converting Between Formats</h2>
            <p className="text-gray-300 mb-4">
              Need to switch between WEBP and JPG? PixelShift makes it instant:
            </p>

            <div className="bg-gradient-to-r from-brand-primary to-brand-secondary rounded-2xl p-8 mb-6">
              <h3 className="text-2xl font-bold text-white mb-4">Convert in 5 Seconds</h3>
              <ul className="space-y-2 text-white mb-6">
                <li>• WEBP → JPG (for compatibility)</li>
                <li>• WEBP → PNG (for transparency)</li>
                <li>• Any image → Any format</li>
              </ul>
              <Link to="/" className="inline-block bg-white text-brand-primary font-bold py-3 px-8 rounded-lg hover:bg-gray-100">
                Convert Now - Free
              </Link>
            </div>

            <div className="bg-gray-800 border-l-4 border-brand-primary p-6 rounded-r-lg mt-8">
              <p className="text-white font-semibold mb-2">💡 Pro Tip</p>
              <p className="text-gray-300">
                Use WEBP for your website, but keep JPG backups for emails and documents. With PixelShift, you can convert between formats instantly whenever needed!
              </p>
            </div>
          </div>

          <div className="mt-12 text-center bg-gray-800 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-white mb-4">Need to Convert?</h3>
            <Link to="/" className="inline-block bg-brand-primary text-white font-bold py-3 px-8 rounded-lg hover:bg-brand-secondary">
              Try PixelShift - Free
            </Link>
          </div>

          <div className="mt-8 text-center">
            <Link to="/blog" className="text-brand-primary hover:underline flex items-center justify-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Blog
            </Link>
          </div>
        </article>
      </div>
    </div>
  );
};

export default BlogPost3;




